import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FacilityInput, FacilitySummary } from '@biyolink/shared';
import { apiClient, getData, patchData, postData } from './client';

/**
 * Alıcı firmanın tesisleri (docs/09 §1.2). Aktif tesis mesafe hesabını ve yakınlık
 * sıralamasını belirlediği için, tesis değişince mesafeye dayalı tüm listeler tazelenmeli.
 */
export const facilityKeys = {
  all: ['facilities'] as const,
};

export function useFacilities(enabled = true) {
  return useQuery({
    queryKey: facilityKeys.all,
    queryFn: () => getData<FacilitySummary[]>('/facilities'),
    enabled,
    staleTime: 60_000,
  });
}

/** Tesis değişimi mesafeye bağlı her listeyi etkiler — hepsini tazeliyoruz. */
function useFacilityInvalidation() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: facilityKeys.all });
    await queryClient.invalidateQueries({ queryKey: ['listings'] });
  };
}

export function useCreateFacility() {
  const invalidate = useFacilityInvalidation();
  return useMutation({
    mutationFn: (input: FacilityInput) => postData<FacilitySummary>('/facilities', input),
    onSuccess: invalidate,
  });
}

export function useUpdateFacility() {
  const invalidate = useFacilityInvalidation();
  return useMutation({
    mutationFn: ({ id, ...input }: FacilityInput & { id: string }) =>
      patchData<FacilitySummary>(`/facilities/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteFacility() {
  const invalidate = useFacilityInvalidation();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/facilities/${id}`);
    },
    onSuccess: invalidate,
  });
}

/** Üst bardaki seçici: aktif tesisi değiştirir. */
export function useSelectFacility() {
  const invalidate = useFacilityInvalidation();
  return useMutation({
    mutationFn: (id: string) => postData<FacilitySummary>(`/facilities/${id}/default`),
    onSuccess: invalidate,
  });
}
